import { ref } from "vue";
import { defineStore } from "pinia";
import {
  getPrimaryLanguage,
  setPrimaryLanguage,
} from "@/services/workspace.service";
import { AUTO_DETECT } from "@/constants/transcriptionLanguages";

// The user's default transcription language. It's saved on the server, so it
// follows them across web, Android and iOS. null means Auto-detect;
// undefined means it hasn't loaded (see languageForRequest).
export const useLanguagePreferenceStore = defineStore(
  "languagePreference",
  () => {
    const defaultLanguage = ref(undefined);
    const loaded = ref(false);
    // Bumped by every load and save. Only the latest may set the value, so
    // an older response arriving late can't overwrite a newer one.
    let version = 0;

    // Reads the saved default. A failed read keeps the last known value.
    async function load() {
      const mine = ++version;
      let result;
      try {
        result = await getPrimaryLanguage();
      } catch {
        // Thrown only when no auth token could be had.
        return defaultLanguage.value;
      }
      const { data, error } = result || {};
      if (mine === version && !error && data) {
        defaultLanguage.value = data.primaryLanguage || null;
        loaded.value = true;
      }
      return defaultLanguage.value;
    }

    // Saves a new default (Auto-detect is stored as null, never "auto").
    // Returns whether it was saved.
    async function save(language) {
      const primaryLanguage =
        language && language !== AUTO_DETECT ? language : null;
      // A load already in flight started before this save: don't let it land.
      version++;
      let result;
      try {
        result = await setPrimaryLanguage(primaryLanguage);
      } catch {
        return false;
      }
      const { data, error } = result || {};
      if (error || !data?.success) return false;
      defaultLanguage.value = data.primaryLanguage ?? primaryLanguage;
      loaded.value = true;
      return true;
    }

    return { defaultLanguage, loaded, load, save };
  },
);
